import type { CaseDetailEntryTab } from "@/components/case-checking/caseReviewNavigation";
import type { CaseShellMode } from "@/components/case-checking/CaseReviewShell";
import type { ReviewWorkflowStep } from "@/components/case-checking/CaseReviewWorkflow";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import { NAV_KEYS } from "@/navigation/keys";
import { stripTransientUrlParams } from "@/navigation/urlTransient";

export type CaseCheckingViewMode = "rules";

const DETAIL_TABS = new Set<CaseDetailEntryTab>([
  "summary",
  "documents",
  "timeline",
  "completeness",
  "assessment",
  "inconsistencies",
  "remedial",
  "artefacts",
  "adjudication",
]);

const SHELL_MODES = new Set<CaseShellMode>(["summary", "review", "detail"]);

const WORKFLOW_STEPS = new Set<ReviewWorkflowStep>([
  "narrative",
  "documents",
  "checklist",
  "rules",
  "assessment",
  "actions",
  "summary",
]);

export type CaseCheckingDetailQuery = {
  tab?: CaseDetailEntryTab;
  shell?: CaseShellMode;
  workflow?: ReviewWorkflowStep;
};

export type CaseCheckingLocation = CaseCheckingDetailQuery & {
  caseId?: string;
  view?: CaseCheckingViewMode;
  focus?: string;
};

function readString(params: Record<string, string | undefined>, key: string): string | undefined {
  return params[key]?.trim() || undefined;
}

function parseDetailFields(params: Record<string, string | undefined>): CaseCheckingDetailQuery {
  const out: CaseCheckingDetailQuery = {};
  const tabRaw = readString(params, NAV_KEYS.tab);
  if (tabRaw && DETAIL_TABS.has(tabRaw as CaseDetailEntryTab)) {
    out.tab = tabRaw as CaseDetailEntryTab;
  }
  const shellRaw = readString(params, "shell");
  if (shellRaw && SHELL_MODES.has(shellRaw as CaseShellMode)) {
    out.shell = shellRaw as CaseShellMode;
  }
  const workflowRaw = readString(params, "workflow");
  if (workflowRaw && WORKFLOW_STEPS.has(workflowRaw as ReviewWorkflowStep)) {
    out.workflow = workflowRaw as ReviewWorkflowStep;
  }
  return out;
}

function detailQueryToParams(query: CaseCheckingDetailQuery): Record<string, string> {
  const out: Record<string, string> = {};
  if (query.tab && query.tab !== "summary") {
    out[NAV_KEYS.tab] = query.tab;
  }
  if (query.shell) {
    out.shell = query.shell;
  }
  if (query.workflow) {
    out.workflow = query.workflow;
  }
  return out;
}

export function normalizeCaseChecking(loc: CaseCheckingLocation): CaseCheckingLocation {
  if (loc.caseId) {
    return { caseId: loc.caseId, ...parseDetailFields(loc) };
  }
  if (loc.view === "rules") {
    return { view: "rules" };
  }
  const next: CaseCheckingLocation = {};
  if (loc.focus) next.focus = loc.focus;
  return next;
}

function fromParams(params: Record<string, string | undefined>): CaseCheckingLocation {
  const caseId = readString(params, "caseId");
  if (caseId) {
    return normalizeCaseChecking({ caseId, ...parseDetailFields(params) });
  }
  if (params[NAV_KEYS.view] === "rules") {
    return { view: "rules" };
  }
  const next: CaseCheckingLocation = {};
  const focus = readString(params, NAV_KEYS.focus);
  if (focus) next.focus = focus;
  return normalizeCaseChecking(next);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): CaseCheckingLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "case-checking") {
    const segment = segments[1];
    if (segment === "rules") {
      params[NAV_KEYS.view] = "rules";
    } else if (segment) {
      params.caseId = decodeURIComponent(segment);
    }
  }
  return fromParams(params);
}

function toParams(loc: CaseCheckingLocation): Record<string, string> {
  const n = normalizeCaseChecking(loc);
  if (n.view === "rules") {
    return { [NAV_KEYS.view]: "rules" };
  }
  if (n.caseId) {
    return { caseId: n.caseId, ...detailQueryToParams(n) };
  }
  const out: Record<string, string> = {};
  if (n.focus) out[NAV_KEYS.focus] = n.focus;
  return out;
}

function toUrl(loc: CaseCheckingLocation): string {
  const n = normalizeCaseChecking(loc);
  if (n.view === "rules") {
    return "/case-checking/rules";
  }
  if (n.caseId) {
    const base = `/case-checking/${encodeURIComponent(n.caseId)}`;
    const qs = new URLSearchParams(stripTransientUrlParams(detailQueryToParams(n))).toString();
    return qs ? `${base}?${qs}` : base;
  }
  const qs = new URLSearchParams(stripTransientUrlParams(toParams(n))).toString();
  return `/case-checking${qs ? `?${qs}` : ""}`;
}

export const caseCheckingCodec: AppLocationCodec<CaseCheckingLocation> = {
  appId: "case-checking",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

/** OS deep link: second path segment after ``/case-checking/`` (or empty for list). */
export function parseCaseCheckingFromRoute(
  resourceId: string | undefined,
  searchParams: URLSearchParams,
): CaseCheckingLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  if (resourceId === "rules") {
    params[NAV_KEYS.view] = "rules";
  } else if (resourceId) {
    params.caseId = decodeURIComponent(resourceId);
  }
  return fromParams(params);
}

export function serializeCaseCheckingToOsParams(loc: CaseCheckingLocation): Record<string, string> {
  return toParams(loc);
}

/** OS / URL params for agent list-highlight navigation to Case Checking. */
export function caseCheckingListFocusParams(focusKey: string): Record<string, string> {
  return caseCheckingCodec.toParams(normalizeCaseChecking({ focus: focusKey }));
}

export function caseCheckingShareUrl(caseId: string, query: CaseCheckingDetailQuery = {}): string {
  return toAbsoluteUrl(caseCheckingCodec.toUrl(normalizeCaseChecking({ caseId, ...query })));
}

export type { CaseDetailEntryTab, CaseShellMode, ReviewWorkflowStep };
